import React from 'react';
import { StylishNewChip } from 'components/DesignSystems/New/StylishNewChip';
import type { StylishNewChipProps } from 'components/DesignSystems/New/StylishNewChip';

/** Represents a data source with its metadata */
interface DataSource {
  /** Unique identifier for the data source */
  source: string;
  /** Whether this is a user-created custom source */
  isCustom?: boolean;
  /** Additional properties that might exist on the source */
  [key: string]: unknown;
}

/** Props for the DataSourceChip component */
interface DataSourceChipProps {
  /** The data source object */
  source: DataSource;
  /** Display name for the chip */
  displayName: string;
  /** Whether this chip is currently selected */
  isSelected: boolean;
  /** Handler to toggle selection state */
  onToggle: () => void;
  /** Handler to edit the data source (optional - only for custom sources) */
  onEdit?: () => void;
  /** Handler to delete the data source (optional - only for custom sources) */
  onDelete?: () => void;
  /**
   * Handler to configure the data source (optional - for sources needing
   * configuration)
   */
  onConfigure?: () => void;
  /** Whether this source needs configuration before use */
  needsConfiguration?: boolean;
  /** Optional children to render inside the chip (e.g., icons) */
  children?: React.ReactNode;
}

/**
 * DataSourceChip - A wrapper component for data source chips Handles all the
 * conditional logic for different chip states and types
 */
const DataSourceChip: React.FC<DataSourceChipProps> = ({
  source,
  displayName,
  isSelected,
  onToggle,
  onEdit,
  onDelete,
  onConfigure,
  needsConfiguration = false,
  children,
}) => {
  // Build props object based on chip state and type
  const chipProps: StylishNewChipProps = {
    label: displayName,
    children,
  };

  // Selected chips always have a delete button to deselect
  if (isSelected) {
    // Special handling for Files datasource only
    if (source.source === 'Files' && onDelete) {
      chipProps.handleDelete = onDelete; // X button removes from sources
      chipProps.handleClick = onToggle; // Click expands/collapses
    } else {
      // All other datasources: X button deselects
      chipProps.handleDelete = onToggle;
    }

    // Selected Incident sources get an edit button (like custom sources)
    if (source.source === 'Incident' && onEdit) {
      chipProps.handleEdit = onEdit;
    }
  } else {
    // Unselected chips have different behaviors based on type
    if (needsConfiguration && onConfigure) {
      // Sources that need configuration open config mode
      chipProps.handleClick = onConfigure;
    } else {
      // Regular sources can be toggled
      chipProps.handleClick = onToggle;
    }

    // Custom sources have edit and delete actions when not selected
    if (source.isCustom) {
      // Only add handlers if they're provided
      if (onEdit) {
        chipProps.handleEdit = onEdit;
      }
      if (onDelete) {
        chipProps.handleDelete = onDelete;
      }
    }
  }

  return <StylishNewChip {...chipProps} />;
};

export default DataSourceChip;
